/* Chair Caps Australia journey measurement v1.0.1 - 2026-08-14 */
(() => {
  'use strict';

  if (window.__ccaJourneyMeasurementV1) return;
  window.__ccaJourneyMeasurementV1 = true;

  const VERSION = '1.0.1';
  const SESSION_KEY = 'cca_journey_session_v1';
  const SESSION_TTL_MS = 30 * 60 * 1000;
  const DEBUG = new URLSearchParams(location.search).get('cca_measurement_debug') === '1';
  const LIVE_QA = new URLSearchParams(location.search).get('cca_measurement_qa') === '1';
  const pendingGa = [];
  const pendingClarity = [];
  const seen = new Set();
  const counts = new Map();
  const lastHelpAt = new WeakMap();
  const debugEvents = [];
  let transportRetry;
  let clarityTransportRetry;
  let consentReadyRetry;
  let consentReadyAttempt = 0;
  let consentBoundInitialized = false;

  const clean = (value, max = 100) => String(value ?? '')
    .replace(/[\r\n\t]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, max);

  const productFamily = (handle = '') => {
    const value = clean(handle, 120).toLowerCase();
    if (value === 'groovy-2-0-floor-protector') return 'round';
    if (value === 'square-silicone-felt-chair-glides') return 'square';
    if (value === 'rectangular-silicone-felt-chair-glides') return 'rectangular';
    if (value === 'oval-silicone-felt-chair-glides') return 'oval';
    if (value === 'groovy-silicone-felt-glide') return 'groovy_flex';
    if (value === 'new-elite-groovy') return 'elite';
    if (value.includes('sled')) return 'sled';
    if (value.includes('gecko')) return 'gecko';
    if (value.includes('silicone')) return 'other_silicone';
    return value ? 'other' : 'not_applicable';
  };

  const pageFamily = (() => {
    if (location.pathname === '/') return 'homepage';
    if (location.pathname.startsWith('/products/')) return 'product';
    if (location.pathname.startsWith('/collections/')) return 'collection';
    if (location.pathname.startsWith('/cart')) return 'cart';
    if (location.pathname.startsWith('/search')) return 'search';
    return 'other';
  })();

  const productMeta = () => window.ShopifyAnalytics?.meta?.product || null;
  const currentHandle = () => productMeta()?.handle || location.pathname.match(/^\/products\/([^/?#]+)/)?.[1] || '';
  const currentFamily = () => productFamily(currentHandle());
  const currentProductId = () => clean(productMeta()?.id || document.querySelector('form[action*="/cart/add"] input[name="product-id"]')?.value, 40);
  const currentVariantId = () => clean(document.querySelector('form[data-type="add-to-cart-form"] input[name="id"]')?.value || productMeta()?.variants?.[0]?.id, 40);
  const themeVersion = () => clean(window.Shopify?.theme?.id || 'unknown', 30);

  const state = {
    measurement_version: VERSION,
    theme_version: themeVersion(),
    page_state_id: `${pageFamily}_v20260813`,
    homepage_merch_state: pageFamily === 'homepage' ? 'round_square_rect_v20260812' : 'not_applicable',
    collection_order_state: location.pathname.includes('/collections/silicone-chair-leg-protection') ? 'round_square_rect_v20260812' : 'not_applicable',
    selector_version: pageFamily === 'product' ? 'cca_selector_20260811' : 'not_applicable',
    offer_state: 'bau_aug2026',
    experiment_id: 'none',
    experiment_variant: 'none',
  };

  const analyticsAllowed = () => {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return privacy?.analyticsProcessingAllowed ? privacy.analyticsProcessingAllowed() === true : false;
    } catch (_) {
      return false;
    }
  };

  const randomRef = () => {
    const bytes = new Uint8Array(12);
    crypto.getRandomValues(bytes);
    return [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  };

  const journey = () => {
    if (!analyticsAllowed()) return null;
    const now = Date.now();
    try {
      const stored = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
      if (stored?.ref && now - Number(stored.last || 0) <= SESSION_TTL_MS) {
        stored.last = now;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(stored));
        return stored.ref;
      }
    } catch (_) {}
    const created = { ref: `cca_${randomRef()}`, last: now };
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify(created)); } catch (_) {}
    return created.ref;
  };

  const context = () => ({
    ...state,
    product_family: currentFamily(),
    journey_ref: journey() || undefined,
    ...(LIVE_QA ? { debug_mode: true, cca_qa: 'true' } : {}),
  });

  const recordDebug = (destination, action, payload) => {
    debugEvents.push({ destination, action, payload, at: Date.now() });
  };

  const flushGa = () => {
    if (typeof window.gtag !== 'function' || !analyticsAllowed()) return false;
    while (pendingGa.length) {
      const [name, params] = pendingGa.shift();
      window.gtag('event', name, params);
    }
    return true;
  };

  const scheduleTransportRetry = () => {
    if (transportRetry) return;
    let attempt = 0;
    const retry = () => {
      transportRetry = null;
      if (flushGa() || attempt >= 4) return;
      attempt += 1;
      transportRetry = window.setTimeout(retry, [250, 750, 1500, 3000][attempt - 1]);
    };
    transportRetry = window.setTimeout(retry, 0);
  };

  const ga = (name, params = {}) => {
    if (!analyticsAllowed()) return;
    const payload = { ...context(), ...params };
    if (DEBUG) return recordDebug('ga4', name, payload);
    pendingGa.push([name, payload]);
    if (!flushGa()) scheduleTransportRetry();
  };

  const flushClarity = () => {
    if (typeof window.clarity !== 'function' || !analyticsAllowed()) return false;
    while (pendingClarity.length) {
      const [action, args] = pendingClarity.shift();
      window.clarity(action, ...args);
    }
    return true;
  };

  const scheduleClarityRetry = () => {
    if (clarityTransportRetry) return;
    let attempt = 0;
    const retry = () => {
      clarityTransportRetry = null;
      if (flushClarity() || attempt >= 4) return;
      attempt += 1;
      clarityTransportRetry = window.setTimeout(retry, [250, 750, 1500, 3000][attempt - 1]);
    };
    clarityTransportRetry = window.setTimeout(retry, 0);
  };

  const clarity = (action, ...args) => {
    if (!analyticsAllowed()) return;
    if (DEBUG) return recordDebug('clarity', action, args);
    pendingClarity.push([action, args]);
    if (!flushClarity()) scheduleClarityRetry();
  };

  const clarityEvent = (name) => clarity('event', name);
  const clarityTag = (key, value) => clarity('set', key, clean(value, 120));

  const identify = () => {
    const ref = journey();
    if (!ref) return;
    clarity('identify', ref, ref, state.page_state_id);
    clarityTag('cca_page_state', state.page_state_id);
    clarityTag('cca_product_family', currentFamily());
    clarityTag('cca_selector_version', state.selector_version);
    clarityTag('cca_offer_state', state.offer_state);
    clarityTag('cca_experiment', `${state.experiment_id}:${state.experiment_variant}`);
    if (LIVE_QA) clarityTag('cca_qa', 'true');
  };

  const cappedIndex = (key) => {
    const next = (counts.get(key) || 0) + 1;
    counts.set(key, next);
    return next === 1 ? '1' : next === 2 ? '2' : '3_plus';
  };

  const itemFromCurrentProduct = () => {
    const meta = productMeta();
    return {
      item_id: currentProductId(),
      item_name: clean(meta?.variants?.[0]?.name?.split(' - ')[0] || document.querySelector('h1')?.textContent, 140),
      item_variant: currentVariantId(),
      item_category: currentFamily(),
    };
  };

  const parseCard = (link) => {
    const href = link?.getAttribute('href') || '';
    const handle = href.match(/\/products\/([^/?#]+)/)?.[1] || '';
    const idMatch = link?.id?.match(/-(\d+)$/);
    const root = link?.closest('.card-wrapper, .product-card-wrapper, li, article');
    const section = link?.closest('.shopify-section, section');
    const uniqueLinks = section ? [...section.querySelectorAll('a[id^="CardLink-"][href*="/products/"]')] : [];
    const unique = uniqueLinks.filter((candidate, index, all) => all.findIndex((other) => other.href === candidate.href) === index);
    const sectionKey = link?.id?.match(/^CardLink-(.+)-\d+$/)?.[1]?.split('__').pop() || section?.id || 'product_list';
    return {
      link,
      root,
      section,
      handle,
      family: productFamily(handle),
      item: {
        item_id: idMatch?.[1] || clean(handle, 80),
        item_name: clean(link?.textContent, 140),
        item_category: productFamily(handle),
        item_list_id: clean(sectionKey, 90),
        item_list_name: clean(section?.querySelector('h1, h2, h3')?.textContent || sectionKey, 120),
        index: Math.max(1, unique.findIndex((candidate) => candidate.href === link.href) + 1),
      },
    };
  };

  const initLists = () => {
    if (!('IntersectionObserver' in window)) return;
    const links = [...document.querySelectorAll('a[id^="CardLink-"][href*="/products/"]')];
    const unique = links.filter((link, index, all) => all.findIndex((other) => other.href === link.href && other.closest('.shopify-section, section') === link.closest('.shopify-section, section')) === index);
    const targetLinks = new WeakMap();
    const observer = new IntersectionObserver((entries) => {
      const batches = new Map();
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
        observer.unobserve(entry.target);
        const card = parseCard(targetLinks.get(entry.target) || entry.target);
        if (!card.item.item_id) continue;
        const key = `${card.item.item_list_id}:${card.item.item_id}`;
        if (seen.has(`list:${key}`)) continue;
        seen.add(`list:${key}`);
        if (!batches.has(card.item.item_list_id)) batches.set(card.item.item_list_id, []);
        batches.get(card.item.item_list_id).push(card.item);
      }
      for (const items of batches.values()) {
        if (items.length) ga('view_item_list', { item_list_id: items[0].item_list_id, item_list_name: items[0].item_list_name, items });
      }
    }, { threshold: [0.5] });
    unique.forEach((link) => {
      const target = link.closest('.card-wrapper, .product-card-wrapper') || link;
      targetLinks.set(target, link);
      observer.observe(target);
    });
  };

  const initPromotion = () => {
    const hero = document.querySelector('.cca-home-hero');
    const button = hero?.querySelector('.cca-home-hero__button');
    if (!hero || !button || !('IntersectionObserver' in window)) return;
    const promotion = { promotion_id: 'cca_home_hero_aug2026', promotion_name: 'shop_chair_caps', creative_name: 'move_chairs_without_scratches', creative_slot: 'homepage_hero' };
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) {
        observer.disconnect();
        ga('view_promotion', { items: [promotion] });
      }
    }, { threshold: [0.5] });
    observer.observe(hero);
    button.addEventListener('click', () => ga('select_promotion', { items: [promotion] }), { passive: true });
  };

  const optionType = (input) => {
    const raw = clean(input?.name || input?.closest('fieldset')?.querySelector('legend')?.textContent, 50).toLowerCase();
    if (raw.includes('size')) return 'size';
    if (raw.includes('pack') || raw.includes('quantity')) return 'pack';
    if (raw.includes('colour') || raw.includes('color')) return 'colour';
    if (raw.includes('shape')) return 'shape';
    return 'other';
  };

  const issueForOption = (input) => {
    const label = input?.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null;
    if (input?.disabled || input?.classList.contains('disabled') || label?.classList.contains('disabled') || input?.getAttribute('aria-disabled') === 'true') return 'unavailable_option';
    return '';
  };

  const sendSelectionIssue = (input, issueType) => {
    const key = `${currentProductId()}:${optionType(input)}:${clean(input?.value, 60)}:${issueType}`;
    if (seen.has(`issue:${key}`)) return;
    seen.add(`issue:${key}`);
    ga('cca_selection_issue', {
      issue_type: issueType,
      option_type: optionType(input),
      option_value: clean(input?.value, 60),
      recoverable: 'true',
      items: [itemFromCurrentProduct()],
    });
    clarityTag('cca_selection_issue', issueType);
    clarityEvent('cca_selection_issue');
  };

  const initOptions = () => {
    document.addEventListener('click', (event) => {
      const label = event.target.closest('label[for]');
      const input = event.target.matches?.('variant-selects input') ? event.target : (label ? document.getElementById(label.htmlFor) : null);
      if (!input?.closest('variant-selects')) return;
      const issue = issueForOption(input);
      if (issue) sendSelectionIssue(input, issue);
    }, true);

    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input?.matches?.('variant-selects input, variant-selects select')) return;
      const type = optionType(input);
      ga('cca_option_change', {
        option_type: type,
        option_value: clean(input.value, 60),
        change_index: cappedIndex(`option:${type}`),
        items: [itemFromCurrentProduct()],
      });
      clarityTag('cca_last_option_type', type);
      clarityEvent('cca_option_change');
      if (issueForOption(input)) return;
      window.setTimeout(() => {
        if (input.matches('input[type="radio"]') && !input.checked) return;
        const submit = document.querySelector('form[data-type="add-to-cart-form"] [type="submit"]');
        if (submit?.disabled && /sold out|unavailable/i.test(submit.textContent || '')) sendSelectionIssue(input, /sold out/i.test(submit.textContent || '') ? 'sold_out_variant' : 'invalid_combination');
      }, 100);
    }, true);
  };

  const helpType = (element) => {
    const text = clean(element?.textContent || element?.getAttribute?.('aria-label'), 180).toLowerCase();
    if (/measure|size|fit/.test(text)) return 'measurement_fit';
    if (/pack|pieces|quantity/.test(text)) return 'pack';
    if (/shipping|delivery|dispatch/.test(text)) return 'shipping';
    if (/return|refund|guarantee|money-back/.test(text)) return 'returns_guarantee';
    if (/chat|help|contact/.test(text)) return 'contact_help';
    return 'faq_other';
  };

  const sendHelp = (element, placement) => {
    const now = Date.now();
    if (now - Number(lastHelpAt.get(element) || 0) < 1000) return;
    lastHelpAt.set(element, now);
    const type = helpType(element);
    ga('cca_help_open', { help_type: type, placement, items: currentProductId() ? [itemFromCurrentProduct()] : undefined });
    clarityTag('cca_help_type', type);
    clarityEvent('cca_help_open');
  };

  const initHelp = () => {
    document.addEventListener('toggle', (event) => {
      const details = event.target;
      if (!details?.matches?.('details') || !details.open) return;
      const summary = details.querySelector(':scope > summary');
      if (!summary || !/measure|fit|size|pack|pieces|help|shipping|delivery|return|guarantee|sample/i.test(summary.textContent || '')) return;
      sendHelp(summary, details.closest('product-info') ? 'pdp_accordion' : 'page_content');
    }, true);
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.htm-guide__video-button, .htm-guide__chat-button, .htm-guide__disclosure-button, [aria-controls*="Measure"], [aria-label*="measur" i]');
      if (button) sendHelp(button, 'measurement_guide');
    }, true);
  };

  const cartErrorText = () => [...document.querySelectorAll('.product-form__error-message, .cart-item__error-text, #cart-errors, [data-cart-error]')]
    .map((element) => clean(element.textContent, 160))
    .find(Boolean) || '';

  const sendCartError = (family = 'cart_add_failed') => {
    const signature = `${family}:${cartErrorText()}`;
    if (seen.has(`cart-error:${signature}`)) return;
    seen.add(`cart-error:${signature}`);
    ga('cca_add_to_cart_error', { error_family: family, recoverable: 'true', items: currentProductId() ? [itemFromCurrentProduct()] : undefined });
    clarityTag('cca_cart_error', family);
    clarityEvent('cca_add_to_cart_error');
  };

  const initCartErrors = () => {
    document.addEventListener('submit', (event) => {
      if (!event.target?.matches?.('form[action*="/cart/add"]')) return;
      [1200, 4000].forEach((delay) => window.setTimeout(() => { if (cartErrorText()) sendCartError(); }, delay));
    }, true);
    document.querySelectorAll('.product-form__error-message, .cart-item__error-text, #cart-errors, [data-cart-error]').forEach((element) => {
      new MutationObserver(() => { if (cartErrorText()) sendCartError(); }).observe(element, { childList: true, characterData: true, subtree: true, attributes: true });
    });
  };

  const quantityBand = (value) => {
    const number = Number(value || 0);
    if (number <= 0) return '0';
    if (number === 1) return '1';
    if (number <= 3) return '2_3';
    if (number <= 7) return '4_7';
    return '8_plus';
  };

  const sendQuantity = (direction, from, to) => {
    const key = `${direction}:${quantityBand(from)}:${quantityBand(to)}`;
    const now = Date.now();
    if (window.__ccaLastQuantityEvent?.key === key && now - window.__ccaLastQuantityEvent.at < 1200) return;
    window.__ccaLastQuantityEvent = { key, at: now };
    ga('cca_cart_quantity_change', { direction, from_band: quantityBand(from), to_band: quantityBand(to) });
    clarityTag('cca_cart_edit', direction);
    clarityEvent('cca_cart_quantity_change');
  };

  const initCartQuantity = () => {
    document.addEventListener('click', (event) => {
      const remove = event.target.closest('cart-remove-button a, cart-remove-button button, [data-cart-remove]');
      if (remove) return sendQuantity('remove', 1, 0);
      const button = event.target.closest('cart-drawer .quantity__button, cart-items .quantity__button');
      if (!button) return;
      const input = button.closest('.quantity')?.querySelector('input[type="number"]');
      const from = Number(input?.value || 0);
      window.setTimeout(() => {
        const to = Number(input?.value || 0);
        if (to !== from) sendQuantity(to > from ? 'increase' : 'decrease', from, to);
      }, 500);
    }, true);
    document.addEventListener('change', (event) => {
      const input = event.target;
      if (!input?.matches?.('cart-drawer input[type="number"], cart-items input[type="number"]')) return;
      const from = Number(input.defaultValue || 0);
      const to = Number(input.value || 0);
      if (to !== from) sendQuantity(to > from ? 'increase' : to === 0 ? 'remove' : 'decrease', from, to);
    }, true);
  };

  const initProductSwitch = () => {
    if (pageFamily !== 'product') return;
    const family = currentFamily();
    try {
      const previous = sessionStorage.getItem('cca_last_product_family_v1');
      if (previous && previous !== family && previous !== 'not_applicable' && family !== 'not_applicable') {
        ga('cca_product_switch', { from_family: clean(previous, 50), to_family: family, switch_index: cappedIndex('product_switch') });
        clarityTag('cca_product_switch', `${previous}_to_${family}`);
        clarityEvent('cca_product_switch');
      }
      sessionStorage.setItem('cca_last_product_family_v1', family);
    } catch (_) {}
  };

  const initSelectItem = () => {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href*="/products/"]');
      if (!link) return;
      const card = parseCard(link);
      if (!card.item.item_id || !card.root) return;
      ga('select_item', { item_list_id: card.item.item_list_id, item_list_name: card.item.item_list_name, items: [card.item] });
      clarityTag('cca_selected_family', card.family);
      clarityEvent('cca_select_item');
    }, true);
  };

  const sourceFamily = (filename = '') => {
    try {
      const url = new URL(filename, location.href);
      if (url.hostname === location.hostname || /shopifycdn\.com$/.test(url.hostname)) return 'theme_asset';
      return 'app_or_third_party';
    } catch (_) { return 'unknown'; }
  };

  const technicalError = (issueType, resourceType, filename) => {
    if ((counts.get('technical_error') || 0) >= 3) return;
    counts.set('technical_error', (counts.get('technical_error') || 0) + 1);
    ga('cca_technical_error', { issue_type: issueType, resource_type: resourceType, source_family: sourceFamily(filename), recoverable: 'unknown' });
    clarityTag('cca_technical_error', issueType);
    clarityEvent('cca_technical_error');
  };

  const initErrors = () => {
    window.addEventListener('error', (event) => {
      const target = event.target;
      if (target && target !== window && /^(SCRIPT|LINK)$/.test(target.tagName || '')) {
        technicalError('resource_load_error', target.tagName.toLowerCase(), target.src || target.href || '');
      } else if (event.filename) {
        technicalError('javascript_error', 'script', event.filename);
      }
    }, true);
    window.addEventListener('unhandledrejection', () => technicalError('unhandled_promise_rejection', 'script', ''), { passive: true });
  };

  const initSearch = () => {
    if (pageFamily !== 'search') return;
    const raw = new URLSearchParams(location.search).get('q') || '';
    const looksSensitive = /@|\b\d{7,}\b/.test(raw);
    const term = looksSensitive ? '[redacted]' : clean(raw, 50).toLowerCase();
    if (term) ga('view_search_results', { search_term: term });
    const resultLinks = document.querySelectorAll('a[id^="CardLink-"][href*="/products/"]');
    if (term && resultLinks.length === 0) {
      ga('cca_selection_issue', { issue_type: 'zero_search_results', option_type: 'search', recoverable: 'true' });
      clarityTag('cca_selection_issue', 'zero_search_results');
      clarityEvent('cca_selection_issue');
    }
  };

  const initConsentBoundState = () => {
    if (consentBoundInitialized) return;
    if (!analyticsAllowed()) {
      if (consentReadyRetry || consentReadyAttempt >= 4) return;
      const delay = [250, 750, 1500, 3000][consentReadyAttempt];
      consentReadyAttempt += 1;
      consentReadyRetry = window.setTimeout(() => {
        consentReadyRetry = null;
        initConsentBoundState();
      }, delay);
      return;
    }
    consentBoundInitialized = true;
    identify();
    initLists();
    initPromotion();
    initProductSwitch();
    initSearch();
  };

  const init = () => {
    initSelectItem();
    initOptions();
    initHelp();
    initCartErrors();
    initCartQuantity();
    initErrors();
    initConsentBoundState();
    document.addEventListener('visitorConsentCollected', initConsentBoundState, { once: true });
  };

  window.CCAJourneyMeasurement = {
    version: VERSION,
    state,
    get debugEvents() { return debugEvents.slice(); },
    get journeyRef() { return DEBUG ? journey() : undefined; },
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
